import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import Newsletter from '../Newsletter';
import { AI_TEAM } from '../../pages/AiTeamPage';
import { LinkedInIcon, FacebookIcon, InstagramIcon } from '../Footer';
import { useT } from '../../i18n';

const SOCIALS = [
  {
    Icon: LinkedInIcon, name: 'LinkedIn', handle: 'kitxenscom', href: 'https://www.linkedin.com/company/kitxenscom',
    iconBg: '#0A66C2',
    hoverCls: 'hover:border-[#0A66C2]/50 hover:shadow-[0_24px_60px_-28px_rgba(10,102,194,0.55)]',
    handleCls: 'group-hover:text-[#0A66C2]',
  },
  {
    Icon: FacebookIcon, name: 'Facebook', handle: 'kitxenscom', href: 'https://www.facebook.com/kitxenscom',
    iconBg: '#1877F2',
    hoverCls: 'hover:border-[#1877F2]/50 hover:shadow-[0_24px_60px_-28px_rgba(24,119,242,0.55)]',
    handleCls: 'group-hover:text-[#1877F2]',
  },
  {
    Icon: InstagramIcon, name: 'Instagram', handle: '@kitxenscom', href: 'https://www.instagram.com/kitxenscom',
    iconBg: 'linear-gradient(45deg, #FEDA75 0%, #FA7E1E 25%, #D62976 55%, #962FBF 80%, #4F5BD5 100%)',
    hoverCls: 'hover:border-[#D62976]/50 hover:shadow-[0_24px_60px_-28px_rgba(214,41,118,0.55)]',
    handleCls: 'group-hover:text-[#D62976]',
  },
];

export default function MagazineExtras() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const team = AI_TEAM.filter((m) => m.active).slice(0, 3);

  return (
    <>
      {/* Meet the AI Editorial Team */}
      <section className="mt-16 rounded-3xl bg-neutral-950 text-white p-8 sm:p-10 overflow-hidden relative" data-testid="magazine-ai-team-strip">
        <div aria-hidden="true" className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#F77F11]/15 blur-[90px] pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
          <div className="lg:flex-1">
            <p className="inline-flex items-center gap-2 rounded-full border border-[#F77F11]/40 bg-[#F77F11]/10 px-4 py-1.5 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#ffb374]">
              <Sparkles className="w-3.5 h-3.5" /> {isEs ? 'EQUIPO EDITORIAL IA' : 'AI EDITORIAL TEAM'}
            </p>
            <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight">
              {isEs ? 'Escrito, ilustrado y distribuido por IA.' : 'Written, illustrated and distributed by AI.'}{' '}
              <span className="text-[#ffb374]">{isEs ? 'Supervisado por operadores reales.' : 'Supervised by real operators.'}</span>
            </h2>
            <p className="mt-3 text-neutral-400 text-[14.5px] max-w-xl">
              {isEs
                ? 'Penny investiga y escribe cada artículo. Sonny lo lleva a tus redes. Rachel responde tus preguntas. Conoce al equipo.'
                : 'Penny researches and writes every article. Sonny takes it to your feeds. Rachel answers your questions. Meet the team.'}
            </p>
          </div>
          <div className="flex items-center gap-5">
            <div className="flex -space-x-3">
              {team.map((m) =>
                m.avatar ? (
                  <img key={m.slug} src={m.avatar} alt={m.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-neutral-950" />
                ) : (
                  <div key={m.slug} className="w-14 h-14 rounded-full bg-[#F77F11] ring-2 ring-neutral-950 flex items-center justify-center font-bold text-white text-lg">
                    {m.name.charAt(0)}
                  </div>
                )
              )}
            </div>
            <Link
              to="/ai-team"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] transition-colors px-6 h-12 font-semibold text-[14.5px]"
              data-testid="magazine-meet-team-link"
            >
              {isEs ? 'Conoce al equipo' : 'Meet the team'} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter (Brevo pipeline) */}
      <section className="mt-10" data-testid="magazine-newsletter">
        <Newsletter />
      </section>

      {/* Follow Kitxens */}
      <section className="mt-10" data-testid="magazine-social-band">
        <p className="kx-eyebrow text-center mb-6">{isEs ? 'SÍGUENOS — SONNY PUBLICA A DIARIO' : 'FOLLOW US — SONNY POSTS DAILY'}</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {SOCIALS.map(({ Icon, name, handle, href, iconBg, hoverCls, handleCls }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noreferrer"
              className={`group flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-5 hover:-translate-y-1 transition-[border-color,box-shadow,transform] duration-300 ${hoverCls}`}
              data-testid={`magazine-social-${name.toLowerCase()}`}
            >
              <span
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-white group-hover:scale-110 transition-transform duration-300"
                style={{ background: iconBg }}
              >
                <Icon className="w-6 h-6" mono />
              </span>
              <span className="min-w-0">
                <span className="block font-bold text-[15px] text-neutral-900">{name}</span>
                <span className={`block text-[13px] text-neutral-500 truncate transition-colors duration-300 ${handleCls}`}>{handle}</span>
              </span>
              <ArrowRight className="w-4 h-4 ml-auto text-neutral-300 group-hover:text-neutral-900 group-hover:translate-x-1.5 transition-[color,transform] duration-300 shrink-0" />
            </a>
          ))}
        </div>
      </section>
    </>
  );
}
